<template>
  <BaseWrapper>
    <div class="space-y-4">
      <div class="flex justify-end gap-2">
        <BaseInput
          type="search"
          placeholder="Pencarian"
          :shadow="false"
          debounce
          v-model="filter.search"
          @native-input="loadExpeditions"
        />
        <BaseButton @click="onAddOpen">Tambah Ekspedisi</BaseButton>
        <BaseButton :loading="loadingExport.visible" @click="onExport">
          <Icon
            v-if="!loadingExport.visible"
            icon="heroicons:arrow-down-tray"
            class="h-4 w-4"
          />
          {{ loadingExport.visible ? loadingExport.text : 'Export' }}
        </BaseButton>
      </div>

      <Datatable
        :total="expeditions.meta.page.total"
        :perPage="expeditions.meta.page.perPage"
        :currentPage="expeditions.meta.page.currentPage"
        @pagechanged="onChangePageData"
        :columns="[
          { id: 'epxedition_code', name: 'Kode' },
          { id: 'epxedition_name', name: 'Nama' },
          { id: 'epxedition_description', name: 'Keterangan' },
          { id: 'action', name: 'Aksi' },
        ]"
      >
        <template #tbody="{ classes }">
          <tr
            v-for="expedition in expeditions.data"
            :key="expedition.id"
            :class="[classes.tr, 'bg-white']"
          >
            <td :class="classes.td">
              <div class="flex items-center gap-2">
                <p class="font-bold text-gray-900">
                  {{ expedition.attributes.code }}
                </p>
                <Icon
                  v-if="expedition.attributes.is_global"
                  class="h-5 w-5 text-blue-600"
                  icon="heroicons:check-badge-20-solid"
                />
                <Icon
                  v-else
                  class="h-5 w-5 text-green-600"
                  icon="heroicons:check-badge-20-solid"
                />
              </div>
            </td>
            <td :class="classes.td">{{ expedition.attributes.name }}</td>
            <td :class="classes.td">
              {{ expedition.attributes.description }}
            </td>
            <td :class="classes.td">
              <BaseButton
                v-if="me.office_type === 'pusat' || !expedition.attributes.is_global"
                class="text-yellow-600 hover:text-yellow-700"
                for-icon
                @click="onEditOpen(expedition)"
              >
                <Icon icon="heroicons:pencil-square-solid" class="h-5 w-5" />
              </BaseButton>
              <BaseButton
                class="text-red-600 hover:text-red-700"
                for-icon
                @click="onDeleteOpen(expedition)"
              >
                <Icon icon="heroicons:trash-solid" class="h-5 w-5" />
              </BaseButton>
            </td>
          </tr>
        </template>
      </Datatable>
    </div>

    <BaseModal :showing="addModal.visible" @close="addModal.visible = false">
      <BaseCard
        :with-style="false"
        :title="addModal.id ? 'Edit Ekspedisi' : 'Tambah Ekspedisi'"
      >
        <form class="space-y-4" @submit.prevent="onAddSubmit">
          <BaseInput
            type="text"
            label="Kode"
            fullwidth
            :shadow="false"
            placeholder="EXP-005"
            v-model="form.code"
            required
          />
          <BaseInput
            type="text"
            label="Nama"
            fullwidth
            :shadow="false"
            placeholder="SiCepat"
            v-model="form.name"
            required
          />
          <BaseInput
            tag="textarea"
            rows="4"
            label="Keterangan"
            fullwidth
            :shadow="false"
            placeholder="Jakarta"
            v-model="form.description"
            required
          />
          <BaseCheckbox v-if="me.office_type === 'pusat'" label="Global" v-model="form.is_global" />
          <div class="flex gap-2">
            <BaseButton :loading="loadingSubmit">Simpan</BaseButton>
            <BaseButton color="light">Batal</BaseButton>
          </div>
        </form>
      </BaseCard>
    </BaseModal>

    <BaseConfirm
      :visible="deleteConfirm.visible"
      icon-type="badge"
      title="Hapus Ekspedisi"
      message="Konfirmasi hapus ekspedisi"
      confirm-text="Hapus"
      @confirmed="onDeleteConfirm"
      @close="deleteConfirm.visible = false"
    />

    <Loading v-if="loadingData || loadingDelete || loadingJobStatus" />
  </BaseWrapper>
</template>

<script>
import { mapGetters } from 'vuex';
import { requestMixin } from '../../mixins/request/request';
import BaseModal from '../../components/base/BaseModal.vue';
import BaseConfirm from '../../components/base/BaseConfirm.vue';
import BaseCheckbox from '../../components/base/BaseCheckbox.vue';
import { mapActions } from 'vuex';
import { downloadFileUrl } from '../../services/utils.service.js';

export default {
  mixins: [requestMixin],
  components: { BaseModal, BaseConfirm, BaseCheckbox },
  data() {
    return {
      expeditions: {
        data: [],
        meta: {
          page: {},
        },
      },
      filter: {
        search: null,
      },
      form: {
        code: null,
        name: null,
        description: null,
        is_global: true,
      },
      loadingData: true,
      loadingSubmit: false,
      loadingDelete: false,
      loadingJobStatus: false,
      loadingExport: {
        visible: false,
        text: null,
      },
      addModal: {
        id: null,
        visible: false,
      },
      deleteConfirm: {
        id: null,
        visible: false,
      },
    };
  },
  computed: {
    ...mapGetters({
      me: 'auth/getUser',
      getChannelName: 'auth/getChannelName',
    }),
  },
  methods: {
    ...mapActions({
      createAlert: 'alert/createAlert'
    }),
    async loadExpeditions(params = {}) {
      this.loadingData = true;

      const [res, err] = await this.request(`/api/v1/expeditions`, {
        params: {
          'page[size]': 5,
          sort: '-createdAt',
          'filter[search]': this.filter.search,
          ...params,
        },
      });

      if (!err) {
        this.expeditions = res;
      }

      this.loadingData = false;
    },
    async loadJobStatus() {
      this.loadingJobStatus = true;

      const [res, err] = await this.request(
        '/api/v1/users/-actions/check-job-status',
        {
          params: {
            type: 'export_expedition',
          },
        }
      );

      if (!err) {
        const { running } = res;

        if (running) {
          this.startLoadingExport('Sedang Diproses');
        }
      }

      this.loadingJobStatus = false;
    },
    startLoadingExport(text) {
      this.loadingExport.text = text;
      this.loadingExport.visible = true;
    },
    stopLoadingExport() {
      this.loadingExport.visible = false;
    },
    onChangePageData(page) {
      this.loadExpeditions({ 'page[number]': page });
    },
    onAddOpen() {
      this.addModal.id = null;
      this.addModal.visible = true;

      this.form.code = null;
      this.form.name = null;
      this.form.description = null;
      this.form.is_global = true;
    },
    onEditOpen(expedition) {
      this.addModal.id = expedition.id;
      this.addModal.visible = true;

      this.form.code = expedition.attributes.code;
      this.form.name = expedition.attributes.name;
      this.form.description = expedition.attributes.description;
      this.form.is_global = expedition.attributes.is_global;
    },
    onDeleteOpen(expedition) {
      this.deleteConfirm.id = expedition.id;
      this.deleteConfirm.visible = true;
    },
    async onDeleteConfirm() {
      this.loadingDelete = true;

      const [, err] = await this.request(
        `/api/v1/expeditions/${this.deleteConfirm.id}`,
        {
          method: 'patch',
          data: {
            data: {
              type: 'expeditions',
              id: this.deleteConfirm.id,
              attributes: {
                deletedAt: new Date(),
              },
            },
          },
        }
      );

      if (!err) {
        this.loadExpeditions();

        this.deleteConfirm.visible = false;
      }

      this.loadingDelete = false;
    },
    async onAddSubmit() {
      this.loadingSubmit = true;

      const [, err] = await this.request(
        this.addModal.id
          ? `/api/v1/expeditions/${this.addModal.id}`
          : `/api/v1/expeditions`,
        {
          method: this.addModal.id ? 'patch' : 'post',
          data: {
            data: {
              type: 'expeditions',
              ...(this.addModal.id ? { id: this.addModal.id } : {}),
              attributes: {
                code: this.form.code,
                name: this.form.name,
                description: this.form.description,
                is_global: this.form.is_global,
              },
            },
          },
        }
      );

      if (err) {
        this.createAlert({
          status: 'error',
          data: this.getRequestErrorMessage(err)
        })
      } else {
        this.addModal.visible = false;

        this.loadExpeditions();
      }

      this.loadingSubmit = false;
    },
    async onExport() {
      this.startLoadingExport('Mengirim ke server');

      const [, err] = await this.request(
        `/api/v1/expeditions/-actions/export`,
        {
          method: 'post'
        }
      );

      if (err) {
        this.createAlert({
          status: 'error',
          data: this.getRequestErrorMessage(err),
        });

        this.stopLoadingExport();
      } else {
        this.startLoadingExport('Sedang diproses');
      }
    },
    onExportProgress(e) {
      if (e.type === 'App\\Notifications\\ExpeditionExported') {
        if (e.status === 'failed') {
          this.stopLoadingExport();

          this.createAlert({
            data: 'Gagal mengexport ekspedisi',
            status: 'error',
          });
        } else if (e.status === 'success') {
          this.stopLoadingExport();

          downloadFileUrl(e.file_url, 'ekspedisi.csv', { blank: true });
        }
      }
    },
    addListener() {
      const userChannel = this.$pusher.channel(this.getChannelName);

      userChannel.bind(
        'Illuminate\\Notifications\\Events\\BroadcastNotificationCreated',
        this.onExportProgress
      );
    },
  },
  created() {
    this.loadExpeditions();
    this.loadJobStatus();
    this.addListener()
  },
};
</script>
